import { useMutation, useQuery } from '@apollo/client';
import { Empty, message, Skeleton } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  ApplicantUserProfessionalInput,
  Entity,
  StaffApplicantProfileProfessionEditContainerApplicantUserDocument,
  StaffApplicantProfileProfessionEditContainerEntitiesDocument,
  StaffApplicantProfileUpdatePersonalInformationProfessionContainerDocument
} from '../../../../generated';
import { Country } from '../../../../types/country';
import { ComponentQueryLoader } from '../../../shared/component-query-loader';
import { ApplicantProfileProfessionEdit } from './applicant-profile-profession-edit';
import { lookupCountryQuery } from './staff-components-country-query';
import { lookupHealthProfessionQuery } from './staff-components-health-profession-query';

interface ApplicantProfileProfessionEditContainerProps {
  applicantId: string;
}

export const ApplicantProfileProfessionEditContainer: React.FC<ApplicantProfileProfessionEditContainerProps> = ({ applicantId }) => {
  const navigate = useNavigate();
  const {
    data: professionData,
    loading: professionLoading,
    error: professionError
  } = useQuery(StaffApplicantProfileProfessionEditContainerApplicantUserDocument, {
    variables: {
      applicantUserId: applicantId
    }
  });

  const { data: countryData, error: countryError } = useQuery(lookupCountryQuery);

  const { data: healthData, loading: healthProfessionLoading, error: healthProfessionError } = useQuery(lookupHealthProfessionQuery);

  // Query to get the entities data
  const { data: entityData, loading: entityLoading, error: entityError } = useQuery(StaffApplicantProfileProfessionEditContainerEntitiesDocument);

  const [applicantUserUpdate] = useMutation(StaffApplicantProfileUpdatePersonalInformationProfessionContainerDocument);

  const handleSave = async (data: ApplicantUserProfessionalInput) => {
    try {
      const result = await applicantUserUpdate({
        variables: {
          input: {
            id: applicantId,
            applicantUserDetails: {
              personalInformation: {
                professional: {
                  citizenshipAtTimeEnteredMedicalInstitution: data.citizenshipAtTimeEnteredMedicalInstitution,
                  healthProfession: data.healthProfession,
                  primaryEducationalInformation: data.primaryEducationalInformation
                }
              }
            }
          }
        }
      });
      if (result.data?.applicantUserUpdate.status.success) {
        message.success('Profession details updated');
        handleEdit();
      } else {
        message.error('Failed to update Profession details');
      }
    } catch (error) {
      message.error(`Error Updating Profession: ${JSON.stringify(error)}`);
    }
  };

  const filteredEntityData = entityData?.entities?.filter((entity) => entity?.isIssuingInstitution === true) || [];

  const handleEdit = () => {
    const currentPath = location.pathname;
    const newPath = currentPath.replace(/\/[^/]+$/, '/view');
    navigate(newPath);
  };

  if (professionLoading || entityLoading || healthProfessionLoading) {
    return <Skeleton active />;
  }

  if (professionError || entityError || healthProfessionError || !professionData?.applicantUser?.personalInformation?.professional) {
    return <Empty description="There was an error loading the data. Please try again!" />;
  }

  if (countryError || !countryData?.countries?.eventPayload?.countries) {
    return <Empty description={`There was an error loading the country data. Please try again! ${countryError}`} />;
  }

  const error = professionError || entityError || healthProfessionError;
  const isLoading = professionLoading || entityLoading || healthProfessionLoading;
  return (
    <ComponentQueryLoader
      error={error}
      loading={isLoading}
      hasData={professionData.applicantUser.personalInformation.professional !== undefined}
      hasDataComponent={
        <ApplicantProfileProfessionEdit
          professionalInformation={professionData.applicantUser.personalInformation.professional}
          onCancel={handleEdit}
          countryData={countryData?.countries?.eventPayload?.countries as Country[]}
          onSave={handleSave}
          entities={filteredEntityData as Entity[]}
          healthProfessions={healthData?.healthProfessions?.healthProfessions}
        />
      }
    />
  );
};
